import { useState, useEffect, useCallback } from 'react';
import { visitorsAPI } from '../../services/api';
import { theme } from '../../theme';

const TABS = ['Today', 'Check-In', 'Pre-Register', 'All Visitors', 'Pass Master', 'Blacklist', 'Reports'];
const PURPOSE_OPTS = ['meeting', 'interview', 'delivery', 'vendor', 'maintenance', 'personal', 'other'];

const INPUT = {
  border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem', fontSize: '0.875rem',
  color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box',
};

const VISITOR_STATUS_STYLE = {
  checked_in:     { background: '#D1FAE5', color: '#065F46' },
  checked_out:    { background: theme.pageBg, color: theme.textMuted },
  pre_registered: { background: '#DBEAFE', color: '#1D4ED8' },
  blacklisted:    { background: '#FEE2E2', color: '#991B1B' },
};

export default function VisitorManagement() {
  const [tab, setTab] = useState('Today');
  const [dashboard, setDashboard] = useState(null);

  const reloadDash = useCallback(() => {
    visitorsAPI.getDashboard().then(r => setDashboard(r.data)).catch(console.error);
  }, []);

  useEffect(() => { reloadDash(); }, [reloadDash]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Visitor Management</h1>
          {dashboard && (
            <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
              {dashboard.currently_inside} inside · {dashboard.available_passes} passes free
            </p>
          )}
        </div>
        <button
          onClick={() => setTab('Check-In')}
          style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
        >
          + Check In Visitor
        </button>
      </div>

      {/* Quick KPIs */}
      {dashboard && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          <KpiCard label="Today's Visitors"  value={dashboard.today_total}            icon="👥" color={theme.info} />
          <KpiCard label="Currently Inside"  value={dashboard.currently_inside}       icon="🏢" color={theme.success} />
          <KpiCard label="Pre-Registered"    value={dashboard.pre_registered_today}   icon="📋" color={theme.primary} />
          <KpiCard label="Available Passes"  value={`${dashboard.available_passes}/${dashboard.total_passes}`} icon="🎫" color={theme.textMuted} />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{
              padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
              whiteSpace: 'nowrap', border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: tab === t ? `2px solid ${theme.primary}` : '2px solid transparent',
              color: tab === t ? theme.primary : theme.textMuted,
              marginBottom: '-1px',
            }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Today'        && <TodayTab onAction={reloadDash} />}
      {tab === 'Check-In'     && <CheckInForm onSuccess={() => { reloadDash(); setTab('Today'); }} />}
      {tab === 'Pre-Register' && <PreRegisterTab />}
      {tab === 'All Visitors' && <AllVisitorsTab />}
      {tab === 'Pass Master'  && <PassMasterTab />}
      {tab === 'Blacklist'    && <BlacklistTab />}
      {tab === 'Reports'      && <ReportsTab />}
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon, color }) {
  return (
    <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem', borderTop: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem' }}>{icon}</span>
        <span style={{ fontSize: '0.75rem', color: theme.textMuted, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text }}>{value}</div>
    </div>
  );
}

// ── Today Tab ─────────────────────────────────────────────────────────────────
function TodayTab({ onAction }) {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    visitorsAPI.getVisitors({}).then(r => setVisitors(r.data.visitors || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkout = async (id) => {
    await visitorsAPI.checkOut(id);
    load();
    onAction();
  };

  return (
    <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading…</div>
      ) : visitors.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No visitors today</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                {['Pass', 'Name', 'Company', 'Purpose', 'Host', 'Check-In', 'Check-Out', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visitors.map((v, i) => (
                <tr key={v.id} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? '#fff' : theme.pageBg }}>
                  <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: theme.textMuted }}>{v.pass_number || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: theme.text }}>{v.name}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.company || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, textTransform: 'capitalize' }}>{v.purpose || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.host_name || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.75rem' }}>
                    {v.check_in_time ? new Date(v.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.75rem' }}>
                    {v.check_out_time ? new Date(v.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{ ...( VISITOR_STATUS_STYLE[v.status] || { background: theme.pageBg, color: theme.textMuted }), fontSize: '0.72rem', padding: '0.2rem 0.6rem', borderRadius: '999px', fontWeight: 500, textTransform: 'capitalize' }}>
                      {v.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {v.status === 'checked_in' && (
                      <button onClick={() => checkout(v.id)}
                        style={{ background: 'none', border: 'none', fontSize: '0.8rem', color: theme.danger, cursor: 'pointer', fontWeight: 500, padding: 0 }}>
                        Check Out
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Check-In Form ─────────────────────────────────────────────────────────────
function CheckInForm({ onSuccess }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', company: '',
    purpose: 'meeting', purpose_detail: '',
    host_employee_id: '', id_proof_type: 'aadhar', id_proof_number: '',
    vehicle_number: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await visitorsAPI.checkIn(form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: theme.text, margin: '0 0 1rem' }}>Visitor Check-In</h2>
        {error && (
          <div style={{ marginBottom: '1rem', background: '#FEF2F2', border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: '0.5rem', padding: '0.6rem 1rem', fontSize: '0.875rem' }}>{error}</div>
        )}
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Full Name *</label>
              <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Phone *</label>
              <input required value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Email</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Company</label>
              <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Purpose</label>
              <select value={form.purpose} onChange={e => setForm(p => ({ ...p, purpose: e.target.value }))} style={{ ...INPUT, width: '100%' }}>
                {PURPOSE_OPTS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Details / Remarks</label>
              <input value={form.purpose_detail} onChange={e => setForm(p => ({ ...p, purpose_detail: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>ID Proof Type</label>
              <select value={form.id_proof_type} onChange={e => setForm(p => ({ ...p, id_proof_type: e.target.value }))} style={{ ...INPUT, width: '100%' }}>
                {['aadhar', 'pan', 'passport', 'dl', 'voter_id', 'other'].map(t => (
                  <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>ID Number</label>
              <input value={form.id_proof_number} onChange={e => setForm(p => ({ ...p, id_proof_number: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Vehicle Number</label>
              <input value={form.vehicle_number} onChange={e => setForm(p => ({ ...p, vehicle_number: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Notes</label>
              <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
            <button type="submit" disabled={saving}
              style={{ padding: '0.6rem 1.5rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Checking In…' : 'Check In & Assign Pass'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Pre-Register Tab ──────────────────────────────────────────────────────────
function PreRegisterTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', company: '',
    purpose: 'meeting', expected_visit_date: '',
    host_employee_id: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = () => {
    visitorsAPI.getPreRegistered({}).then(r => setList(r.data || [])).catch(console.error);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await visitorsAPI.preRegister(form);
      setForm({ name: '', phone: '', email: '', company: '', purpose: 'meeting', expected_visit_date: '', host_employee_id: '', notes: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to pre-register');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ maxWidth: '640px', background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: theme.text, margin: '0 0 1rem' }}>Pre-Register Visitor</h2>
        <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Name *</label>
            <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Phone *</label>
            <input required value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Company</label>
            <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Expected Visit Date *</label>
            <input required type="date" value={form.expected_visit_date} onChange={e => setForm(p => ({ ...p, expected_visit_date: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Purpose</label>
            <select value={form.purpose} onChange={e => setForm(p => ({ ...p, purpose: e.target.value }))} style={{ ...INPUT, width: '100%' }}>
              {PURPOSE_OPTS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Notes</label>
            <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', paddingTop: '0.25rem' }}>
            <button type="submit" disabled={saving}
              style={{ padding: '0.5rem 1.25rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Pre-Register'}
            </button>
          </div>
        </form>
      </div>

      {list.length > 0 && (
        <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1.25rem', borderBottom: `1px solid ${theme.border}` }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Upcoming Pre-Registrations</h3>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                {['Name', 'Phone', 'Company', 'Purpose', 'Expected Date'].map(h => (
                  <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((v, i) => (
                <tr key={v.id} style={{ borderBottom: i < list.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                  <td style={{ padding: '0.6rem 1rem', fontWeight: 500, color: theme.text }}>{v.name}</td>
                  <td style={{ padding: '0.6rem 1rem', color: theme.textMuted }}>{v.phone}</td>
                  <td style={{ padding: '0.6rem 1rem', color: theme.textMuted }}>{v.company || '—'}</td>
                  <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, textTransform: 'capitalize' }}>{v.purpose}</td>
                  <td style={{ padding: '0.6rem 1rem', color: theme.textMuted }}>{v.expected_visit_date ? new Date(v.expected_visit_date).toLocaleDateString('en-IN') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── All Visitors Tab ──────────────────────────────────────────────────────────
function AllVisitorsTab() {
  const [visitors, setVisitors] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    visitorsAPI.getAllVisitors(params)
      .then(r => { setVisitors(r.data.visitors || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...INPUT, width: '12rem' }} />
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={INPUT} />
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={INPUT} />
        <span style={{ fontSize: '0.875rem', color: theme.textMuted }}>{total} records</span>
      </div>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                  {['Name', 'Phone', 'Company', 'Purpose', 'Check-In', 'Check-Out', 'Status'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visitors.map((v, i) => (
                  <tr key={v.id} style={{ borderBottom: i < visitors.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                    <td style={{ padding: '0.6rem 1rem', fontWeight: 500, color: theme.text }}>{v.name}</td>
                    <td style={{ padding: '0.6rem 1rem', color: theme.textMuted }}>{v.phone}</td>
                    <td style={{ padding: '0.6rem 1rem', color: theme.textMuted }}>{v.company || '—'}</td>
                    <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, textTransform: 'capitalize' }}>{v.purpose || '—'}</td>
                    <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, fontSize: '0.75rem' }}>{v.check_in_time ? new Date(v.check_in_time).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, fontSize: '0.75rem' }}>{v.check_out_time ? new Date(v.check_out_time).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, fontSize: '0.75rem', textTransform: 'capitalize' }}>{v.status?.replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pass Master Tab ───────────────────────────────────────────────────────────
function PassMasterTab() {
  const [passes,   setPasses]   = useState([]);
  const [passId,   setPassId]   = useState('');
  const [adding,   setAdding]   = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    visitorsAPI.getPasses({}).then(r => setPasses(r.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const val = passId.trim().toUpperCase();
    if (!val) return;
    setAdding(true); setError(''); setSuccess('');
    try {
      await visitorsAPI.createPass(val);
      setPassId('');
      setSuccess(`Pass "${val}" added successfully.`);
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to add pass.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete pass "${p.pass_number}"?`)) return;
    try {
      await visitorsAPI.deletePass(p.id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete pass.');
    }
  };

  const available = passes.filter(p => p.is_available).length;
  const inUse     = passes.length - available;

  const filtered  = passes.filter(p =>
    !search || p.pass_number.toLowerCase().includes(search.toLowerCase()) ||
    (p.current_visitor_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* ── Add Pass Form ── */}
      <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem 1.5rem' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: theme.text, margin: '0 0 0.25rem' }}>Add Visitor Pass</h3>
        <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0 0 1rem' }}>
          Enter a unique Pass ID (e.g. VIS-001, PASS-A, GATE-05). Each physical pass card should have one entry.
        </p>

        {error   && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.6rem 1rem', color: '#DC2626', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{error}</div>}
        {success && <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '0.5rem', padding: '0.6rem 1rem', color: '#166534', fontSize: '0.82rem', marginBottom: '0.75rem' }}>✓ {success}</div>}

        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>
              Pass ID *
            </label>
            <input
              required
              value={passId}
              onChange={e => { setPassId(e.target.value); setError(''); }}
              placeholder="e.g. VIS-001"
              style={{ ...INPUT, width: '100%', fontFamily: 'monospace', fontSize: '0.95rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}
            />
          </div>
          <button type="submit" disabled={adding || !passId.trim()}
            style={{
              padding: '0.55rem 1.4rem', background: adding ? '#FDBA74' : theme.primary,
              color: '#fff', border: 'none', borderRadius: '0.5rem',
              fontSize: '0.875rem', fontWeight: 600,
              cursor: adding || !passId.trim() ? 'not-allowed' : 'pointer',
              opacity: !passId.trim() ? 0.6 : 1, transition: 'all 0.15s',
            }}>
            {adding ? 'Adding…' : '+ Add Pass'}
          </button>
        </form>
      </div>

      {/* ── Summary + Search ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <span style={{ background: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0', borderRadius: '999px', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: 700 }}>
            ✓ {available} Available
          </span>
          <span style={{ background: '#FFF1F2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: '999px', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: 700 }}>
            ● {inUse} In Use
          </span>
          <span style={{ background: theme.pageBg, color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: '999px', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: 700 }}>
            {passes.length} Total
          </span>
        </div>
        <input
          type="text" placeholder="Search pass ID or visitor…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...INPUT, width: 220 }}
        />
      </div>

      {/* ── Pass Table ── */}
      <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.75rem', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading passes…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎫</div>
            <p style={{ fontWeight: 600, color: theme.text, margin: '0 0 0.25rem' }}>
              {passes.length === 0 ? 'No passes added yet' : 'No passes match your search'}
            </p>
            <p style={{ color: theme.textMuted, fontSize: '0.82rem', margin: 0 }}>
              {passes.length === 0 ? 'Use the form above to add your first visitor pass ID.' : 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                {['Pass ID', 'Status', 'Assigned To', 'Company', ''].map(h => (
                  <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${theme.border}` : 'none', background: i % 2 === 0 ? '#fff' : theme.pageBg }}>
                  <td style={{ padding: '0.7rem 1rem', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem', color: theme.text, letterSpacing: '0.04em' }}>
                    {p.pass_number}
                  </td>
                  <td style={{ padding: '0.7rem 1rem' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      background: p.is_available ? '#F0FDF4' : '#FFF1F2',
                      color:      p.is_available ? '#166534'  : '#991B1B',
                      border: `1px solid ${p.is_available ? '#BBF7D0' : '#FECACA'}`,
                      padding: '0.2rem 0.7rem', borderRadius: '999px',
                      fontSize: '0.75rem', fontWeight: 700,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.is_available ? '#22C55E' : '#EF4444' }} />
                      {p.is_available ? 'Available' : 'In Use'}
                    </span>
                  </td>
                  <td style={{ padding: '0.7rem 1rem', color: p.current_visitor_name ? theme.text : theme.textMuted, fontWeight: p.current_visitor_name ? 500 : 400 }}>
                    {p.current_visitor_name || '—'}
                  </td>
                  <td style={{ padding: '0.7rem 1rem', color: theme.textMuted }}>
                    {p.current_visitor_company || '—'}
                  </td>
                  <td style={{ padding: '0.7rem 1rem', textAlign: 'right' }}>
                    {p.is_available && (
                      <button onClick={() => handleDelete(p)}
                        style={{ background: 'none', border: '1px solid #FECACA', borderRadius: '0.375rem', padding: '0.2rem 0.6rem', fontSize: '0.75rem', color: '#EF4444', cursor: 'pointer', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FFF1F2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Blacklist Tab ─────────────────────────────────────────────────────────────
function BlacklistTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    visitorsAPI.getBlacklist().then(r => setList(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Remove from blacklist?')) return;
    await visitorsAPI.unblacklist(id);
    load();
  };

  return (
    <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading…</div>
      ) : list.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No blacklisted visitors</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
              {['Name', 'Phone', 'Company', 'Reason', ''].map(h => (
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((v, i) => (
              <tr key={v.id} style={{ borderBottom: i < list.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: theme.text }}>{v.name}</td>
                <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.phone}</td>
                <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.company || '—'}</td>
                <td style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: theme.danger }}>{v.blacklist_reason || '—'}</td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <button onClick={() => remove(v.id)}
                    style={{ background: 'none', border: 'none', fontSize: '0.8rem', color: theme.primary, cursor: 'pointer', padding: 0 }}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Reports Tab ───────────────────────────────────────────────────────────────
function ReportsTab() {
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    visitorsAPI.getDailyLog(reportDate)
      .then(r => setReport(r.data))
      .finally(() => setLoading(false));
  }, [reportDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '640px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.textMuted }}>Date:</label>
        <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} style={INPUT} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0' }}>Loading…</div>
      ) : report ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            <StatBox value={report.total} label="Total Visitors" color={theme.info} />
            <StatBox value={report.currently_inside} label="Still Inside" color={theme.success} />
            <StatBox value={report.checked_out} label="Checked Out" color={theme.textMuted} />
          </div>

          {report.by_purpose && Object.keys(report.by_purpose).length > 0 && (
            <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: '0 0 0.75rem' }}>By Purpose</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {Object.entries(report.by_purpose).map(([p, c]) => (
                  <span key={p} style={{ background: theme.lightBg, color: theme.primary, padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 500, textTransform: 'capitalize' }}>
                    {p}: {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {report.visitors?.length > 0 && (
            <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${theme.border}` }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Visitor Log</h3>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                    {['Name', 'Company', 'Purpose', 'In', 'Out'].map(h => (
                      <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.visitors.map((v, i) => (
                    <tr key={v.id} style={{ borderBottom: i < report.visitors.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                      <td style={{ padding: '0.6rem 1rem', fontWeight: 500, color: theme.text }}>{v.name}</td>
                      <td style={{ padding: '0.6rem 1rem', color: theme.textMuted }}>{v.company || '—'}</td>
                      <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, textTransform: 'capitalize' }}>{v.purpose || '—'}</td>
                      <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, fontSize: '0.75rem' }}>{v.check_in_time ? new Date(v.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td style={{ padding: '0.6rem 1rem', color: theme.textMuted, fontSize: '0.75rem' }}>{v.check_out_time ? new Date(v.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function StatBox({ value, label, color }) {
  return (
    <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem', textAlign: 'center', borderTop: `3px solid ${color}` }}>
      <p style={{ fontSize: '1.75rem', fontWeight: 700, color: theme.text, margin: 0 }}>{value}</p>
      <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>{label}</p>
    </div>
  );
}
